import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { trackAddToCart } from "@/lib/meta";
import { ga4AddToCart } from "@/lib/ga4";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem("aerwear_cart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("aerwear_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, size, color, quantity = 1, variantId = null, bundlePairs = null) => {
    setItems((prev) => {
      const vid = variantId || `${product.id}-${size}-${color}`;
      const existing = prev.find((i) => i.variant_id === vid);
      if (existing) {
        return prev.map((i) =>
          i.variant_id === vid
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, {
        product_id: product.id,
        variant_id: vid,
        name: product.name,
        price: product.price,
        size,
        color,
        quantity,
        image: product.images?.[0] || "",
        gender: product.gender,
        bundlePairs: bundlePairs || null,
      }];
    });
    setIsOpen(true);
    trackAddToCart(product, quantity);
    ga4AddToCart(product, quantity);
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((i) => i.variant_id !== variantId));
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    if (quantity <= 0) {
      removeItem(variantId);
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        i.variant_id === variantId ? { ...i, quantity } : i
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  const value = useMemo(() => ({
    items, isOpen, setIsOpen, addItem, updateQuantity, removeItem, clearCart, total, count
  }), [items, isOpen, setIsOpen, addItem, updateQuantity, removeItem, clearCart, total, count]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
